import { useEffect, useMemo, useState } from "react";
import { Loading } from "../../components/Loading";
import { financeApi } from "./apiService";
import { usePlanStatus } from "../../hooks/usePlanStatus";
import NavigationHeader from "../../components/NavigationHeader";
import { useToast } from "../../components/Toast";
import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  Pagination,
  Badge,
  SelectChangeEvent,
  Paper,
  TextField,
  Button,
} from "@mui/material";
import { formatDate, formatText, formatCurrency } from "../../utils/utils";
import { useResponsiveBreakpoints } from "../../utils/responsiveUtils";
import { getTransactionTypeColor, PaymentModeFormatter } from "./Transactions";
import {
  tableHeaderStyles,
  tableRowStyles,
  smallButtonProp,
  tableCellStyles,
  commonDateFieldProps,
} from "../../utils/styles";
import CashBookPaymentModal from "./components/CashBookPaymentModal";
import AddIcon from "@mui/icons-material/Add";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ConfirmationDialog from "../../project/project_module/components/ConfirmationDialog";
import OpeningBalanceModal from "./components/OpeningBalanceModal";

const CashBook = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [transactions, setTransactions] = useState<any>([]);
  const [cashBookData, setCashBookData] = useState<any>(null);
  const [pagination, setPagination] = useState<any>(null);
  const [pageSize, setPageSize] = useState<number>(20);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);

  // Use responsive breakpoints for layout optimization
  const { isMobile, isTablet, isSmallDesktop } = useResponsiveBreakpoints();
  const [updatingCashbook, setIsUpdatingCashBook] = useState<boolean>(false);
  const [showOpeningBalanceModal, setShowOpeningBalanceModal] =
    useState<boolean>(false);

  // Date filter state - default to today
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split("T")[0]; // Format: YYYY-MM-DD
  });

  const isToday = useMemo(() => {
    return selectedDate === new Date().toISOString().split("T")[0];
  }, [selectedDate]);

  const { planStatus } = usePlanStatus();
  const { showToast } = useToast();

  const getCashBook = async (date: string = selectedDate) => {
    try {
      setIsLoading(true);
      const response = await financeApi.getCashBookData(date);
      setTransactions(response?.data);
      setCashBookData(response?.cashBookOfDate);
      setPagination(response?.pagination);
      if (!response?.cashBookOfDate?.hasPreviousCashbook) {
        setShowOpeningBalanceModal(true);
      }
    } catch (error) {
      showToast();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (planStatus?.company?.id) {
      getCashBook(selectedDate);
    } else {
      setIsLoading(false);
    }
  }, [planStatus]);

  // Handle date filter change
  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(event.target.value);
    getCashBook(event.target.value);
  };

  // Handle applying date filter
  const handleApplyDateFilter = () => {
    if (selectedDate) {
      getCashBook(selectedDate);
    }
  };

  // Handle clearing date filter
  const handleClearDateFilter = () => {
    const today = new Date();
    const todayString = today.toISOString().split("T")[0];
    setSelectedDate(todayString);
    getCashBook(todayString);
  };

  const handleLockCashBook = async () => {
    try {
      setIsUpdatingCashBook(true);
      if (cashBookData?.id && cashBookData?.isLocked) {
        const response = await financeApi.patchCashBook(
          cashBookData?.id,
          false
        );
        if (response) {
          setShowConfirmationModal(false);
          showToast("Cash book unlocked successfully", "success");
          getCashBook(selectedDate);
        }
      } else {
        let response = null;
        if (!cashBookData?.id) {
          response = await financeApi.createCashBook(
            selectedDate,
            cashBookData?.totalCashCredit + cashBookData?.totalOnlineCredit, // totalCredit
            cashBookData?.totalCashDebit + cashBookData?.totalOnlineDebit, // totalDebit
            cashBookData?.currentCashBalance +
              cashBookData?.currentOnlineBalance, // totalBalance
            cashBookData?.currentCashBalance,
            cashBookData?.currentOnlineBalance
          );
        } else {
          response = await financeApi.updateCashBook(cashBookData?.id, {
            date: cashBookData?.date,
            totalCredit:
              cashBookData?.totalCashCredit + cashBookData?.totalOnlineCredit, // totalCredit
            totalDebit:
              cashBookData?.totalCashDebit + cashBookData?.totalOnlineDebit, // totalDebit
            totalBalance:
              cashBookData?.currentCashBalance +
              cashBookData?.currentOnlineBalance,
            totalCashBalance: cashBookData?.currentCashBalance,
            totalOnlineBalance: cashBookData?.currentOnlineBalance,
            isLocked: true,
          });
        }
        if (response) {
          setShowConfirmationModal(false);
          showToast("Cash book locked successfully", "success");
          getCashBook(selectedDate);
        }
      }
    } catch (error) {
      showToast();
    } finally {
      setIsUpdatingCashBook(false);
    }
  };

  if (isLoading) {
    return <Loading message="Loading cash book data..." fullScreen={true} />;
  }

  return (
    <>
      <NavigationHeader
        title="Cash Book"
        backNavigationEnabled={true}
        backNavigationTo="/finance"
        breadcrumbs={[
          { label: "Finance", path: "/finance" },
          { label: "Cash Book" },
        ]}
        buttons={[
          {
            label: cashBookData?.isLocked ? "Unlock" : "Lock",
            onClick: () => {
              setShowConfirmationModal(true);
            },
            icon: cashBookData?.isLocked ? (
              <LockOpenIcon sx={{ fontSize: "1rem" }} />
            ) : (
              <LockIcon sx={{ fontSize: "1rem" }} />
            ),
            variant: cashBookData?.isLocked ? "contained" : "outlined",
          },
          ...(cashBookData?.isLocked
            ? []
            : [
                {
                  label: "Add Transaction",
                  onClick: () => {
                    setShowPaymentModal(true);
                  },
                  icon: <AddIcon sx={{ fontSize: "1.25rem" }} />,
                },
              ]),
        ]}
      />
      <>
        {/* Date filter  */}
        <Paper sx={{ p: 1, mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                flex: 1,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: "150px" },
                }}
              >
                <TextField
                  fullWidth
                  type="date"
                  label="Select Date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  {...commonDateFieldProps}
                  inputProps={{
                    max: new Date().toISOString().split("T")[0],
                  }}
                />
                {!isToday && (
                  <Button
                    variant="text"
                    onClick={() => {
                      setSelectedDate(new Date().toISOString().split("T")[0]);
                      getCashBook(new Date().toISOString().split("T")[0]);
                    }}
                    {...smallButtonProp}
                  >
                    Today
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Summary Cards */}
        <Box
          sx={{
            mb: 1.5,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 1 : 1.5,
            flexWrap: "wrap",
          }}
        >
          {cashBookData && (
            <>
              {/* Opening Balance Card */}
              <Paper
                sx={{
                  p: isMobile ? 1 : 1.25,
                  flex: isMobile ? "1 1 100%" : "1 1 calc(25% - 12px)",
                  minWidth: isMobile ? "auto" : "180px",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: isMobile ? "0.75rem" : "0.8rem",
                    fontWeight: 600,
                    mb: 0.75,
                    color: "text.primary",
                    lineHeight: 1.2,
                  }}
                >
                  Opening Balance
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isMobile ? "0.65rem" : "0.7rem",
                        color: "text.secondary",
                      }}
                    >
                      Cash:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.75rem",
                        fontWeight: 600,
                        color: "#4caf50",
                      }}
                    >
                      {formatCurrency(cashBookData?.previousCashBalance)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isMobile ? "0.65rem" : "0.7rem",
                        color: "text.secondary",
                      }}
                    >
                      Bank:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.75rem",
                        fontWeight: 600,
                        color: "#4caf50",
                      }}
                    >
                      {formatCurrency(cashBookData?.previousOnlineBalance)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Credit Card */}
              <Paper
                sx={{
                  p: isMobile ? 1 : 1.25,
                  flex: isMobile ? "1 1 100%" : "1 1 calc(25% - 12px)",
                  minWidth: isMobile ? "auto" : "180px",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: isMobile ? "0.75rem" : "0.8rem",
                    fontWeight: 600,
                    mb: 0.75,
                    color: "text.primary",
                    lineHeight: 1.2,
                  }}
                >
                  Total Received
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isMobile ? "0.65rem" : "0.7rem",
                        color: "text.secondary",
                      }}
                    >
                      Cash:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.75rem",
                        fontWeight: 600,
                        color: "#4caf50",
                      }}
                    >
                      {formatCurrency(cashBookData?.totalCashCredit)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isMobile ? "0.65rem" : "0.7rem",
                        color: "text.secondary",
                      }}
                    >
                      Bank:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.75rem",
                        fontWeight: 600,
                        color: "#4caf50",
                      }}
                    >
                      {formatCurrency(cashBookData?.totalOnlineCredit)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Debit Card */}
              <Paper
                sx={{
                  p: isMobile ? 1 : 1.25,
                  flex: isMobile ? "1 1 100%" : "1 1 calc(25% - 12px)",
                  minWidth: isMobile ? "auto" : "180px",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: isMobile ? "0.75rem" : "0.8rem",
                    fontWeight: 600,
                    mb: 0.75,
                    color: "text.primary",
                    lineHeight: 1.2,
                  }}
                >
                  Total Paid
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isMobile ? "0.65rem" : "0.7rem",
                        color: "text.secondary",
                      }}
                    >
                      Cash:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.75rem",
                        fontWeight: 600,
                        color: "#f44336",
                      }}
                    >
                      {formatCurrency(cashBookData?.totalCashDebit)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isMobile ? "0.65rem" : "0.7rem",
                        color: "text.secondary",
                      }}
                    >
                      Bank:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.75rem",
                        fontWeight: 600,
                        color: "#f44336",
                      }}
                    >
                      {formatCurrency(cashBookData?.totalOnlineDebit)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Current Balance Card */}
              <Paper
                sx={{
                  p: isMobile ? 1 : 1.25,
                  flex: isMobile ? "1 1 100%" : "1 1 calc(25% - 12px)",
                  minWidth: isMobile ? "auto" : "180px",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: isMobile ? "0.75rem" : "0.8rem",
                    fontWeight: 600,
                    mb: 0.75,
                    color: "text.primary",
                    lineHeight: 1.2,
                  }}
                >
                  Current Balance
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isMobile ? "0.65rem" : "0.7rem",
                        color: "text.secondary",
                      }}
                    >
                      Cash:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.75rem",
                        fontWeight: 600,
                        color:
                          (cashBookData?.currentCashBalance || 0) >= 0
                            ? "#4caf50"
                            : "#f44336",
                      }}
                    >
                      {formatCurrency(cashBookData?.currentCashBalance)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isMobile ? "0.65rem" : "0.7rem",
                        color: "text.secondary",
                      }}
                    >
                      Bank:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.75rem",
                        fontWeight: 600,
                        color:
                          (cashBookData?.currentOnlineBalance || 0) >= 0
                            ? "#4caf50"
                            : "#f44336",
                      }}
                    >
                      {formatCurrency(cashBookData?.currentOnlineBalance)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </>
          )}
        </Box>

        {transactions?.length > 0 ? (
          <>
            <TableContainer sx={{ mb: 2 }} component={Paper}>
              <Table
                size="small"
                sx={{
                  "& .MuiTableCell-root": {
                    padding: "4px 8px",
                    fontSize: "0.75rem",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeaderStyles}>Date</TableCell>
                    <TableCell sx={tableHeaderStyles}>Details</TableCell>
                    <TableCell sx={tableHeaderStyles}>Payment Mode</TableCell>
                    <TableCell sx={tableHeaderStyles}>Type</TableCell>
                    <TableCell sx={tableHeaderStyles}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        sx={{ textAlign: "center", py: 4 }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No transactions found for the selected date range. Try
                          adjusting your filter or clear filters to view all
                          transactions.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : transactions?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        sx={{ textAlign: "center", py: 4 }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No transactions found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactions?.map((transaction: any, index: number) => {
                      const formattedTransactionType =
                        transaction?.transactionType;
                      // transaction?.transactionType === "Paid"
                      //   ? "Debit"
                      //   : transaction?.transactionType === "Received"
                      //   ? "Credit"
                      //   : transaction?.transactionType;
                      return (
                        <TableRow key={transaction?.id || index}>
                          <TableCell sx={tableCellStyles}>
                            {formatDate(transaction.date)}
                          </TableCell>
                          <TableCell sx={tableCellStyles}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                                width: "100%",
                              }}
                            >
                              {transaction?.transactionDetails ? (
                                transaction.transactionDetails
                                  .split("|")
                                  .map((part: any, index: number) => (
                                    <Typography
                                      key={index}
                                      variant="body2"
                                      sx={{
                                        color:
                                          index === 0
                                            ? "text.primary"
                                            : "text.secondary",
                                        fontSize:
                                          index === 0 ? "0.75rem" : "0.65rem",
                                      }}
                                    >
                                      {part.trim()}
                                    </Typography>
                                  ))
                              ) : transaction?.description ? (
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: "0.75rem" }}
                                >
                                  {transaction?.description}
                                </Typography>
                              ) : (
                                <Typography variant="body2">-</Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={tableCellStyles}>
                            {PaymentModeFormatter[
                              transaction?.paymentMode as keyof typeof PaymentModeFormatter
                            ] || "-"}
                          </TableCell>
                          <TableCell sx={tableCellStyles}>
                            <Badge
                              badgeContent={formattedTransactionType}
                              color={
                                ["Received", "Credit"].includes(
                                  formattedTransactionType
                                )
                                  ? "success"
                                  : "error"
                              }
                              sx={{
                                "& .MuiBadge-badge": {
                                  fontSize: "0.65rem",
                                  height: 16,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "5px",
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell sx={tableCellStyles}>
                            {formatCurrency(transaction?.amount || 0)}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              px: 3,
              backgroundColor: "grey.50",
              borderRadius: 2,
              border: "2px dashed",
              borderColor: "grey.300",
            }}
          >
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No Cash Book Data Available for {formatDate(selectedDate)}
            </Typography>
          </Box>
        )}
      </>
      <CashBookPaymentModal
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onTransactionAdded={() => {
          setShowPaymentModal(false);
          getCashBook(selectedDate);
        }}
      />
      <ConfirmationDialog
        deleteModalOpen={showConfirmationModal}
        handleNo={() => setShowConfirmationModal(false)}
        handleYes={handleLockCashBook}
        title={cashBookData?.isLocked ? "Unlock CashBook" : "Lock CashBook"}
        message={`Are you sure you want to ${
          cashBookData?.isLocked ? "unlock" : "lock"
        } the cash book?`}
        itemToDelete={null}
        action={cashBookData?.isLocked ? "Unlock" : "Lock"}
        isLoading={updatingCashbook}
      />
      <OpeningBalanceModal
        open={showOpeningBalanceModal}
        onClose={() => setShowOpeningBalanceModal(false)}
        selectedDate={selectedDate}
        getCashBook={() => getCashBook(selectedDate)}
      />
    </>
  );
};

export default CashBook;
